import React, { useState, useRef, useEffect } from 'react';
import {useNavigate, useLocation} from 'react-router-dom';import {useTranslation} from 'react-i18next';import {BookOpen, Settings, LogOut, Menu, BarChart3, PanelLeftClose, PanelLeft, Sparkles, MoreVertical, Star, LogIn, Home, Users, Bell, HelpCircle} from 'lucide-react';import {Button} from '@/components/ui/button';import axios from 'axios';
import {useSubscription} from '@/contexts/SubscriptionContext';import {UpgradePrompt} from '@/components/UpgradePrompt';import IconTooltip from '@/components/IconTooltip';
import WhatsNewModal, { useChangelogBadge } from '@/components/WhatsNewModal';
import {API_URL as API} from '@/utils/apiConfig';
import { useFeatureFlag } from '@/contexts/FeatureFlagsContext';
// ── Notification Bell (real-time SSE with polling fallback) ──────────────────
function NotificationBell() {
  const {
    t
  } = useTranslation();

  const navigate = useNavigate();
  const [count, setCount] = useState(0);

  useEffect(() => {
    let active = true;
    let es = null;
    let fallbackIv = null;

    const connectSSE = () => {
      try {
        es = new EventSource(`${API}/community/notifications/stream`, { withCredentials: true });
        es.onmessage = (event) => {
          if (!active) return;
          try {
            const data = JSON.parse(event.data);
            setCount(data.count || 0);
          } catch { /* ignore parse errors */ }
        };
        es.onerror = () => {
          // SSE failed — close and fall back to polling
          es.close();
          es = null;
          if (active && !fallbackIv) {
            fallbackIv = setInterval(poll, 30000);
          }
        };
      } catch {
        // EventSource not supported — use polling
        if (active && !fallbackIv) {
          fallbackIv = setInterval(poll, 30000);
        }
      }
    };

    const poll = async () => {
      try {
        const res = await axios.get(`${API}/community/notifications/unread-count`, { withCredentials: true, timeout: 5000 });
        if (active) setCount(res.data.count || 0);
      } catch { /* ignore */ }
    };

    // Initial fetch + connect SSE
    poll();
    connectSSE();

    return () => {
      active = false;
      if (es) es.close();
      if (fallbackIv) clearInterval(fallbackIv);
    };
  }, []);

  return (
    <IconTooltip label={t('generated.header_notifications_60b1')} side="bottom">
      <Button
        variant="ghost" size="icon"
        onClick={() => navigate('/community')}
        className="hover:bg-[var(--theme-hover)] rounded-full theme-text-secondary hover:text-[var(--theme-accent)] relative"
        data-testid="notification-bell-button"
      >
        <Bell className="h-5 w-5" />
        {count > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] px-1 flex items-center justify-center text-[10px] font-bold rounded-full ring-2 ring-[var(--theme-surface)]"
            style={{ background: '#ef4444', color: '#fff' }}
            data-testid="notification-badge"
          >
            {count > 9 ? '9+' : count}
          </span>
        )}
      </Button>
    </IconTooltip>
  );
}

export default function Header({ user, showBibleReader, setShowBibleReader, showSettingsPanel, setShowSettingsPanel, onNewChat, onMobileMenuClick, desktopSidebarOpen, onToggleDesktopSidebar }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const { hasFeature } = useSubscription();
  // Feature-flag gates for nav items — hides icons and menu entries when
  // the corresponding flag is OFF (admins still see them via admin preview).
  const { enabled: communityFlag } = useFeatureFlag('community');
  const { enabled: recapFlag } = useFeatureFlag('weekly_recap');
  const [upgradeFeature, setUpgradeFeature] = useState(null);
  const { hasNew, markSeen } = useChangelogBadge();
  const [showWhatsNew, setShowWhatsNew] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);
  const isChatPage = location.pathname === '/chat';
  const isSettingsPage = location.pathname === '/settings';
  const isGuest = !!user?.is_guest;
  const isRecapPage = location.pathname === '/recap';
  const isLandingPage = location.pathname === '/';
  const isAnalyticsPage = location.pathname === '/analytics';
  const isHomePage = location.pathname === '/app';

  // Close overflow menu on outside click
  useEffect(() => {
    if (!moreOpen) return;
    const handler = (e) => { if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [moreOpen]);

  const handleLogout = async () => {
    try {
      // Disassociate the OneSignal device from this user so future
      // pushes don't keep arriving on a logged-out device.
      try {
        const { logoutOneSignalWeb } = await import('../utils/oneSignalWeb');
        await logoutOneSignalWeb();
      } catch { /* ignore */ }
      // Re-arm the Android native bridge guard so the NEXT user who
      // logs in on this device gets their own AndroidNative.setUserId(uid)
      // call. OneSignal.login(newUid) called from the native side
      // correctly transfers the device subscription off the previous
      // external_id, so no explicit logout-side bridge call is needed.
      try {
        const { resetAndroidLinker } = await import('../utils/oneSignalAndroidLinker');
        resetAndroidLinker();
      } catch { /* ignore */ }
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/login');
    }
  };

  if (isLandingPage || isHomePage) return null;

  const activeIcon = showBibleReader ? 'bible'
    : showSettingsPanel ? 'settings'
    : isRecapPage ? 'recap'
    : isAnalyticsPage ? 'analytics'
    : isSettingsPage ? 'settings'
    : null;

  const iconCls = (key) =>
    `hover:bg-[var(--theme-hover)] rounded-full ${activeIcon === key ? 'text-[var(--theme-accent)] bg-[var(--theme-accentLight)]' : 'theme-text-secondary hover:text-[var(--theme-accent)]'}`;

  return (
    <header className="theme-surface border-b theme-border px-4 md:px-6 sticky top-0 z-[60] shadow-sm transition-colors duration-200" style={{ paddingLeft: 'max(1rem, var(--sal))', paddingRight: 'max(1rem, var(--sar))' }}>
      <div className="flex items-center justify-between relative z-[61] py-3">
        <div className="flex items-center gap-3">
          {isChatPage && onMobileMenuClick && (
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden hover:bg-[var(--theme-hover)] rounded-full"
              onClick={onMobileMenuClick}
              data-testid="mobile-menu-button"
            >
              <Menu className="h-5 w-5 theme-text" />
            </Button>
          )}
          {isChatPage && onToggleDesktopSidebar && (
            <Button
              variant="ghost"
              size="icon"
              className="hidden lg:flex hover:bg-[var(--theme-hover)] rounded-full theme-text-secondary hover:text-[var(--theme-accent)]"
              onClick={onToggleDesktopSidebar}
              title={desktopSidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
              data-testid="desktop-sidebar-toggle"
            >
              {desktopSidebarOpen ? <PanelLeftClose className="h-5 w-5" /> : <PanelLeft className="h-5 w-5" />}
            </Button>
          )}
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/app')}>
            <img src="/logo.png" alt={t('generated.header_eili_f5dd')} className="h-8 w-8 object-contain" />
            <span className="text-xl font-bold theme-text" style={{ fontFamily: "'Playwrite ES Deco', cursive" }}>{t('generated.header_eili_f5dd')}</span>
          </div>
        </div>

        {/* Desktop: show all icons */}
        <div className="hidden md:flex items-center gap-1">
          <IconTooltip label={t('generated.header_home_3c7a')} side="bottom">
          <Button variant="ghost" size="icon" onClick={() => navigate('/app')} className="hover:bg-[var(--theme-hover)] rounded-full theme-text-secondary hover:text-[var(--theme-accent)]" data-testid="home-button">
            <Home className="h-5 w-5" />
          </Button>
          </IconTooltip>
          {setShowBibleReader && (
            <IconTooltip label={t('bible.reader', 'Bible Reader')} side="bottom">
            <Button variant="ghost" size="icon" onClick={() => setShowBibleReader(!showBibleReader)} className={iconCls('bible')} data-testid="bible-reader-button">
              <BookOpen className="h-5 w-5" />
            </Button>
            </IconTooltip>
          )}
          {recapFlag && (
          <IconTooltip label={t('recap.title', 'Recap')} side="bottom">
          <Button variant="ghost" size="icon" onClick={() => { if (hasFeature('recap')) navigate('/recap'); else setUpgradeFeature('recap'); }} className={iconCls('recap')} data-testid="recap-button">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2.5 12a9.5 9.5 0 1 0 2.8-6.7"/><polyline points="2.5 3 2.5 8.5 8 8.5"/><text x="12" y="16" textAnchor="middle" fill="currentColor" stroke="none" fontSize="11" fontWeight="700" fontFamily="sans-serif">R</text></svg>
          </Button>
          </IconTooltip>
          )}
          <IconTooltip label={t('analytics.title', 'Analytics')} side="bottom">
          <Button variant="ghost" size="icon" onClick={() => navigate('/analytics')} className={iconCls('analytics')} data-testid="analytics-button">
            <BarChart3 className="h-5 w-5" />
          </Button>
          </IconTooltip>
          <IconTooltip label={t('generated.header_what_s_new_a6bb')} side="bottom">
          <Button variant="ghost" size="icon" onClick={() => { setShowWhatsNew(true); markSeen(); }} className="hover:bg-[var(--theme-hover)] rounded-full theme-text-secondary hover:text-[var(--theme-accent)] relative" data-testid="whats-new-button">
            <Sparkles className="h-5 w-5" />
            {hasNew && <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-red-500 ring-2 ring-[var(--theme-surface)]" data-testid="whats-new-badge" />}
          </Button>
          </IconTooltip>
          <IconTooltip label={t('generated.header_reviews_18f3')} side="bottom">
          <Button variant="ghost" size="icon" onClick={() => navigate('/reviews')} className="hover:bg-[var(--theme-hover)] rounded-full theme-text-secondary hover:text-[var(--theme-accent)]" data-testid="reviews-button">
            <Star className="h-5 w-5" />
          </Button>
          </IconTooltip>
          {!isGuest && communityFlag && (
            <IconTooltip label={t('generated.header_community_cc9c')} side="bottom">
            <Button variant="ghost" size="icon" onClick={() => navigate('/community')} className="hover:bg-[var(--theme-hover)] rounded-full theme-text-secondary hover:text-[var(--theme-accent)]" data-testid="community-button">
              <Users className="h-5 w-5" />
            </Button>
            </IconTooltip>
          )}
          {!isGuest && communityFlag && <NotificationBell />}
          <IconTooltip label={t('faq.title', 'Help & FAQ')} side="bottom">
          <Button variant="ghost" size="icon" onClick={() => navigate('/faq')} className={iconCls('faq')} data-testid="faq-button">
            <HelpCircle className="h-5 w-5" />
          </Button>
          </IconTooltip>
          <IconTooltip label={t('settings.title', 'Settings')} side="bottom">
          <Button variant="ghost" size="icon" onClick={() => { if (setShowSettingsPanel) setShowSettingsPanel(prev => !prev); else navigate('/settings'); }} className={iconCls('settings')} data-testid="settings-button">
            <Settings className="h-5 w-5" />
          </Button>
          </IconTooltip>
          {isGuest ? (
            <IconTooltip label={t('generated.header_sign_in_890b')} side="bottom">
            <Button variant="ghost" size="icon" onClick={() => navigate('/login')} className="hover:bg-[var(--theme-hover)] rounded-full theme-text-secondary hover:text-[var(--theme-accent)]" data-testid="signin-button">
              <LogIn className="h-5 w-5" />
            </Button>
            </IconTooltip>
          ) : (
            <IconTooltip label={t('settings.logout', 'Log out')} side="bottom">
            <Button variant="ghost" size="icon" onClick={handleLogout} className="hover:bg-destructive/10 hover:text-destructive rounded-full theme-text-secondary" data-testid="logout-button">
              <LogOut className="h-5 w-5" />
            </Button>
            </IconTooltip>
          )}
        </div>

        {/* Mobile: Bible, Recap, Settings + overflow menu (logout moved inside) */}
        <div className="flex md:hidden items-center gap-0.5">
          {setShowBibleReader && (
            <IconTooltip label={t('bible.reader', 'Bible Reader')} side="bottom">
            <Button variant="ghost" size="icon" onClick={() => setShowBibleReader(!showBibleReader)} className={`${iconCls('bible')} h-9 w-9`} data-testid="bible-reader-button-mobile">
              <BookOpen className="h-5 w-5" />
            </Button>
            </IconTooltip>
          )}
          {recapFlag && (
          <IconTooltip label={t('recap.title', 'Recap')} side="bottom">
          <Button variant="ghost" size="icon" onClick={() => { if (hasFeature('recap')) navigate('/recap'); else setUpgradeFeature('recap'); }} className={`${iconCls('recap')} h-9 w-9`} data-testid="recap-button-mobile">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2.5 12a9.5 9.5 0 1 0 2.8-6.7"/><polyline points="2.5 3 2.5 8.5 8 8.5"/><text x="12" y="16" textAnchor="middle" fill="currentColor" stroke="none" fontSize="10" fontWeight="700" fontFamily="sans-serif">R</text></svg>
          </Button>
          </IconTooltip>
          )}
          <IconTooltip label={t('settings.title', 'Settings')} side="bottom">
          <Button variant="ghost" size="icon" onClick={() => { if (setShowSettingsPanel) setShowSettingsPanel(prev => !prev); else navigate('/settings'); }} className={`${iconCls('settings')} h-9 w-9`} data-testid="settings-button-mobile">
            <Settings className="h-5 w-5" />
          </Button>
          </IconTooltip>
          {/* Overflow menu with logout inside */}
          <div ref={moreRef} className="relative">
            <IconTooltip label={t('generated.header_more_66ec')} side="bottom">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMoreOpen(!moreOpen)}
              className="hover:bg-[var(--theme-hover)] rounded-full theme-text-secondary h-9 w-9 relative"
              data-testid="mobile-more-menu-button"
            >
              <MoreVertical className="h-5 w-5" />
              {hasNew && <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-red-500 ring-2 ring-[var(--theme-surface)]" />}
            </Button>
            </IconTooltip>
            {moreOpen && (
              <div className="absolute right-0 top-full mt-1 w-48 rounded-xl theme-surface border theme-border shadow-xl py-1 z-[70] animate-in fade-in slide-in-from-top-2 duration-150" data-testid="mobile-overflow-menu">
                <button
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm theme-text hover:bg-[var(--theme-hover)] transition-colors"
                  onClick={() => { navigate('/app'); setMoreOpen(false); }}
                  data-testid="mobile-home-button"
                >
                  <Home className="h-4 w-4 theme-text-secondary" />
                  {t('generated.header_home_3c7a')}
                </button>
                <button
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm theme-text hover:bg-[var(--theme-hover)] transition-colors"
                  onClick={() => { navigate('/analytics'); setMoreOpen(false); }}
                  data-testid="mobile-analytics-button"
                >
                  <BarChart3 className="h-4 w-4 theme-text-secondary" />
                  {t('analytics.title', 'Analytics')}
                </button>
                <button
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm theme-text hover:bg-[var(--theme-hover)] transition-colors relative"
                  onClick={() => { setShowWhatsNew(true); markSeen(); setMoreOpen(false); }}
                  data-testid="mobile-whats-new-button"
                >
                  <Sparkles className="h-4 w-4 theme-text-secondary" />
                  {t('generated.header_what_s_new_a6bb')}
                  {hasNew && <span className="h-2 w-2 rounded-full bg-red-500 ml-auto" />}
                </button>
                <button
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm theme-text hover:bg-[var(--theme-hover)] transition-colors"
                  onClick={() => { navigate('/reviews'); setMoreOpen(false); }}
                  data-testid="mobile-reviews-button"
                >
                  <Star className="h-4 w-4 theme-text-secondary" />
                  {t('generated.header_reviews_18f3')}
                </button>
                {!isGuest && communityFlag && (
                  <button
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm theme-text hover:bg-[var(--theme-hover)] transition-colors"
                    onClick={() => { navigate('/community'); setMoreOpen(false); }}
                    data-testid="mobile-community-button"
                  >
                    <Users className="h-4 w-4 theme-text-secondary" />
                    {t('generated.header_community_cc9c')}
                  </button>
                )}
                <button
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm theme-text hover:bg-[var(--theme-hover)] transition-colors"
                  onClick={() => { navigate('/faq'); setMoreOpen(false); }}
                  data-testid="mobile-faq-button"
                >
                  <HelpCircle className="h-4 w-4 theme-text-secondary" />
                  {t('faq.title', 'Help & FAQ')}
                </button>
                <div className="my-1 border-t theme-border" />
                {isGuest ? (
                  <button
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm theme-text hover:bg-[var(--theme-hover)] transition-colors"
                    onClick={() => { navigate('/login'); setMoreOpen(false); }}
                    data-testid="mobile-signin-button"
                  >
                    <LogIn className="h-4 w-4 theme-text-secondary" />
                    {t('generated.header_sign_in_890b')}
                  </button>
                ) : (
                  <button
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-destructive hover:bg-destructive/10 transition-colors"
                    onClick={() => { handleLogout(); setMoreOpen(false); }}
                    data-testid="mobile-logout-button"
                  >
                    <LogOut className="h-4 w-4" />
                    {t('settings.logout', 'Log out')}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {upgradeFeature && (
        <UpgradePrompt feature={upgradeFeature} onClose={() => setUpgradeFeature(null)} />
      )}
      <WhatsNewModal open={showWhatsNew} onClose={() => setShowWhatsNew(false)} />
    </header>
  );
}
